import React, { useState, useCallback, useRef } from "react";

import { QuizSection } from "../shared/QuizSection";
import {
  BreedQuestion,
  AgeQuestion,
  SexQuestion,
  ReproductionQuestion,
} from "../questions";
import { useRegisterSubSteps } from "@/state/SubStepContext";

type Dog = { id: string; form: any };

type Props = {
  dogs: Dog[];
  updateDog: (dogId: string, update: any) => void;
};

type SubStep = "breed" | "age" | "sex" | "reproduction";

const SUB_STEPS: SubStep[] = ["breed", "age", "sex", "reproduction"];

/* ---- per-dog completion checks (a sub-step advances when EVERY dog passes) ---- */

function isBreedComplete(form: any): boolean {
  // For "Other", require custom breed name; otherwise selecting is enough.
  if (!form?.breed) return false;
  return form.breed === "Other" ? !!form.customBreed?.trim() : true;
}

function isAgeComplete(form: any): boolean {
  const y = typeof form?.years === "number" ? form.years : 0;
  const m = typeof form?.months === "number" ? form.months : 0;
  return y > 0 || m > 0;
}

export default function StepBasic({ dogs, updateDog }: Props) {
  const firstForm = dogs[0]?.form ?? {};

  const getFurthestSubStep = (): SubStep => {
    if (!dogs.every((d) => isBreedComplete(d.form))) return "breed";
    if (!dogs.every((d) => isAgeComplete(d.form))) return "age";
    if (!dogs.every((d) => !!d.form?.sex)) return "sex";
    return "reproduction";
  };

  // Initial sub-step: saved value (on the first dog) if valid, else first incomplete.
  const getInitialSubStep = (): SubStep => {
    const saved = firstForm.subStepBasic as SubStep;
    if (saved && SUB_STEPS.includes(saved)) return saved;
    return getFurthestSubStep();
  };

  const [subStep, setSubStepLocal] = useState<SubStep>(getInitialSubStep);

  // Use refs to avoid stale closures in memoized callbacks
  const updateDogRef = useRef(updateDog);
  updateDogRef.current = updateDog;
  const firstDogIdRef = useRef(dogs[0]?.id);
  firstDogIdRef.current = dogs[0]?.id;

  const handleStepClick = useCallback((stepId: string) => {
    setSubStepLocal(stepId as SubStep);
    if (firstDogIdRef.current) {
      updateDogRef.current(firstDogIdRef.current, { subStepBasic: stepId });
    }
  }, []);

  const dogsRef = useRef(dogs);
  dogsRef.current = dogs;

  const canAdvanceSubStep = useCallback((step: string): boolean => {
    const all = dogsRef.current;
    switch (step) {
      case "breed":
        return all.every((d) => isBreedComplete(d.form));
      case "age":
        return all.every((d) => isAgeComplete(d.form));
      case "sex":
        return all.every((d) => !!d.form?.sex);
      case "reproduction":
        return all.every((d) => !!d.form?.reproduction);
      default:
        return false;
    }
  }, []);

  useRegisterSubSteps(SUB_STEPS, subStep, handleStepClick, canAdvanceSubStep);

  return (
    <QuizSection>
      {subStep === "breed" &&
        dogs.map((dog) => (
          <BreedQuestion
            key={dog.id}
            dogName={dog.form?.name}
            value={dog.form?.breed}
            customBreed={dog.form?.customBreed}
            onChange={(newBreed, newCustomBreed) =>
              updateDog(dog.id, { breed: newBreed, customBreed: newCustomBreed || "" })
            }
          />
        ))}

      {subStep === "age" &&
        dogs.map((dog) => (
          <AgeQuestion
            key={dog.id}
            dogName={dog.form?.name}
            years={dog.form?.years as number | ""}
            months={dog.form?.months as number | ""}
            onChange={(update) => updateDog(dog.id, update)}
          />
        ))}

      {subStep === "sex" &&
        dogs.map((dog) => (
          <SexQuestion
            key={dog.id}
            dogName={dog.form?.name}
            value={(dog.form?.sex as string) || ""}
            onChange={(newSex) => updateDog(dog.id, { sex: newSex })}
          />
        ))}

      {subStep === "reproduction" &&
        dogs.map((dog) => (
          <ReproductionQuestion
            key={dog.id}
            dogName={dog.form?.name}
            sex={(dog.form?.sex as string) || ""}
            value={(dog.form?.reproduction as string) || ""}
            onChange={(newReproduction) => updateDog(dog.id, { reproduction: newReproduction })}
          />
        ))}
    </QuizSection>
  );
}
